// Copyright Amazon.com, Inc. or its affiliates. All Rights Reserved.
// SPDX-License-Identifier: Apache-2.0

import { useState } from "react"
import type {
  PantryConfirmedCandidate,
  PantryExtractionResponse,
  PantryResponse,
} from "@bitebalance/contracts"
import { useApiClient } from "@/api/useApiClient"
import { useI18n } from "@/i18n"

export interface PantryConfirmResult {
  success: boolean
  /** Owner incident 2026-08-30 ("I added rice, it wasn't added"): an unmatched
   *  candidate silently vanished after confirm — callers must SAY when the
   *  server couldn't match something, for every entry path, not only the
   *  original text quick-add this was first fixed for. */
  unmatchedSkipped: number
}

/**
 * The extraction -> confirmation -> persist state machine, shared by every
 * way of adding a Kitchen item (Kitchen redesign, 2026-09-14 — web twin of
 * mobile/src/lib/usePantryCapture.ts): text quick-add, structured manual
 * entry, and barcode scan all produce a plain sourceText string and end at
 * the same confirmation step — extraction never writes; only the user's
 * confirmation persists.
 */
export function usePantryCapture(onPersisted: (result: PantryResponse) => void) {
  const api = useApiClient()
  const { locale } = useI18n()
  const [extraction, setExtraction] = useState<PantryExtractionResponse | null>(null)
  const [extracting, setExtracting] = useState(false)
  const [confirming, setConfirming] = useState(false)
  const [confirmError, setConfirmError] = useState(false)
  const [pendingExpiry, setPendingExpiry] = useState<string | null>(null)

  async function runExtraction(
    sourceText: string,
    expiryDate: string | null = null
  ): Promise<boolean> {
    setExtracting(true)
    setPendingExpiry(expiryDate)
    try {
      const result = await api.extractPantryItems({ sourceText, locale })
      setExtraction(result)
      return true
    } catch {
      return false
    } finally {
      setExtracting(false)
    }
  }

  async function confirmCandidates(
    candidates: PantryConfirmedCandidate[]
  ): Promise<PantryConfirmResult> {
    if (!extraction) return { success: false, unmatchedSkipped: 0 }
    setConfirming(true)
    setConfirmError(false)
    try {
      const result = await api.confirmPantryItems({
        confirmationToken: extraction.confirmationToken,
        candidates,
      })
      const unmatchedSkipped = candidates.filter(
        c => c.include && c.canonicalIngredientId === null
      ).length
      setExtraction(null)
      setPendingExpiry(null)
      onPersisted(result)
      return { success: true, unmatchedSkipped }
    } catch {
      setConfirmError(true)
      return { success: false, unmatchedSkipped: 0 }
    } finally {
      setConfirming(false)
    }
  }

  function cancelExtraction() {
    setExtraction(null)
    setPendingExpiry(null)
  }

  function dismissConfirmError() {
    setConfirmError(false)
  }

  return {
    extraction,
    extracting,
    confirming,
    confirmError,
    pendingExpiry,
    runExtraction,
    confirmCandidates,
    cancelExtraction,
    dismissConfirmError,
  }
}
